import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import { mediaBelow } from '../../assets/styles/helper';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ComingSoonContainer = styled.div`
  width: calc(100% - 32px);
  min-height: 55px;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    ${themeColors.colorInfoBannerBackground} 0%,
    ${themeColors.colorInfoBannerBackground2} 100%
  );
  color: #fff;
  margin: 16px;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.25);
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.3s ease-in-out;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  ${mediaBelow(variables.breakpoints.bpSm)} {
    margin: 8px;
    padding: 8px 12px;
    gap: 4px;
  }
`;

const Header = styled.h2`
  color: ${themeColors.colorWhite};
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.5px;
  line-height: 1.2;

  ${mediaBelow(variables.breakpoints.bpLg)} {
    font-size: 18px;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const LearnMoreButton = styled.a`
  margin-right: 40px;
  margin-left: 6px;
  background: transparent;
  color: ${themeColors.colorWhite};
  border: 1px solid ${themeColors.colorWhite};
  border-radius: ${variables.borderRadius.borderRadiusCapsule};
  padding: 6px 24px;
  font-size: 16px;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;

  &:visited {
    color: ${themeColors.colorWhite};
    background: transparent;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  &:active {
    color: ${themeColors.colorWhite};
    background: transparent;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    position: absolute;
    top: 6px;
    right: 36px;
    margin: 0;
    padding: 2px 16px;
    font-size: 14px;
    z-index: 1;
  }
`;

const Body = styled.p`
  color: ${themeColors.colorWhite};
  font-size: 13px;
  font-weight: 500;
  margin: 2px 0 0 0;
  opacity: 0.95;
  letter-spacing: 0.3px;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: 14px;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 12px;
    margin-top: 12px;
    line-height: 1.2;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0px;
  right: 20px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 30px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
  ${mediaBelow(variables.breakpoints.bpMd)} {
    top: -4px;
    right: 12px;
  }
`;

const ComingSoonBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const closed = retrieveFromSessionStorage('comingSoonClosed');
    if (closed === 'true') {
      setVisible(false);
    }
  }, []);

  const handleClose = () => {
    setVisible(false);
    storeInSessionStorage('comingSoonClosed', 'true');
  };

  if (!visible) return null;

  return (
    <ComingSoonContainer>
      <CloseButton onClick={handleClose}>×</CloseButton>

      <TextContainer>
        <Header>{t('comingSoon.title')}</Header>
        <Body>{t('comingSoon.subtitle')}</Body>
      </TextContainer>

      <LearnMoreButton
        href="https://marketing.hardassetsalliance.com/hard-assets-alliance-is-becoming-gbi-direct"
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('comingSoon.learnMore')}
      </LearnMoreButton>
    </ComingSoonContainer>
  );
};

export default ComingSoonBanner;
